import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ActionButton, PageLayout } from '../../components/DesignSystem';
import { Button, Card, Input, Select, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import API_URL from '../../config/api';
import { apiFetch } from '../../utils/apiFetch';
import { ListPagination, paginateList } from '../../components/ListPagination';
import '../../styles/listPage.css';

const OrderList: React.FC = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [listPage, setListPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [customerName, setCustomerName] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const rawCustomerId = searchParams.get('customer_id');
  // Validate and parse customer ID - must be a valid number
  const customerId = rawCustomerId && rawCustomerId !== 'null' && rawCustomerId !== 'undefined' && !isNaN(Number(rawCustomerId)) 
    ? rawCustomerId 
    : null;

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) setSearchTerm(q);
  }, [searchParams]);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        setLoading(true);

        // Reset filters when URL-based filtering is active
        if (customerId) {
          setSearchTerm('');
        }

        // Fetch customer name if filtering by customer
        if (customerId) {
          try {
            const customerRes = await apiFetch(`${API_URL}/customers/${customerId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (customerRes.ok) {
              const customer = await customerRes.json();
              setCustomerName(customer.name);
            } else {
              console.error(`❌ Customer ${customerId} not found (status ${customerRes.status})`);
              setCustomerName(null);
            }
          } catch (e) {
            console.error('Failed to fetch customer name:', e);
            setCustomerName(null);
          }
        } else {
          setCustomerName(null);
        }

        const url = customerId
          ? `${API_URL}/orders?customer_id=${customerId}`
          : `${API_URL}/orders`;
        
        const res = await apiFetch(url, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!res.ok) throw new Error(`Status ${res.status}`);

        const data = await res.json();
        // normalize possible _id vs id
        const normalized = Array.isArray(data)
          ? data.map((o: any) => ({ ...o, id: o.id || o._id }))
          : [];
        setOrders(normalized);
      } catch (err) {
        console.error('❌ Failed to fetch orders:', err);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [token, navigate, customerId]);

  useEffect(() => {
    setListPage(1);
  }, [searchTerm, statusFilter, customerId]);

  const isOnlineShopOrder = (order: any) =>
    String(order.notes || '').toLowerCase().includes('online shop') ||
    String(order.order_number || '').startsWith('SHOP-');

  // Apply filters
  const filteredOrders = orders.filter(order => {
    // Search filter (order ID, customer name, description/notes, products)
    const searchLower = searchTerm.toLowerCase();
    const searchMatch = !searchTerm ||
      String(order.id || '').toLowerCase().includes(searchLower) ||
      String(order.order_number || '').toLowerCase().includes(searchLower) ||
      String(order.customerName || order.client_name || '').toLowerCase().includes(searchLower) ||
      String(order.notes || '').toLowerCase().includes(searchLower) ||
      order.items?.some((item: any) => item.productName?.toLowerCase().includes(searchLower));

    if (!searchMatch) return false;

    // Status filter — 'Online Shop' is a special virtual filter
    if (statusFilter === 'Online Shop') return isOnlineShopOrder(order);
    const matchesStatus = statusFilter === 'All' || order.status === statusFilter;

    return matchesStatus;
  });

  const { pages: orderPages, safePage: safeListPage, slice: paginatedOrders } =
    paginateList(filteredOrders, listPage);

  const formatOrderDate = (order: any) =>
    order.orderDate || order.order_date || order.created_at
      ? new Date(order.orderDate || order.order_date || order.created_at).toLocaleDateString('en-GB')
      : 'N/A';

  const renderOrderActions = (order: any) => (
    <div style={s.actions}>
      <ActionButton variant="view" onClick={() => navigate(`/orders/view/${order.id}`)} />
      <ActionButton variant="edit" onClick={() => navigate(`/orders/edit/${order.id}`)} />
    </div>
  );



  if (loading) {
    return <p style={{ padding: 40, textAlign: 'center' }}>⏳ Loading orders...</p>;
  }

  return (
    <PageLayout
      title="🛒 Order List"
      action={<ActionButton variant="add" label="Create Order" onClick={() => navigate('/orders/new')} />}
    >
      {/* Customer Filter Banner (from URL parameter) */}
      {customerId && customerName && (
        <div style={s.filterBanner}>
          <span style={s.filterText}>
            📋 Showing orders for: <strong>{customerName}</strong>
          </span>
          <button 
            style={s.clearFilterButton}
            onClick={() => navigate('/orders/list')}
          >
            ✕ Show All Orders
          </button>
        </div>
      )}

      <Card>
      {/* Search and Filters */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <Space wrap>
          <Input
            placeholder="Search by order ID, customer, description, or product..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 350 }}
          />
          
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 180 }}
          >
            <Select.Option value="All">All Status</Select.Option>
            <Select.Option value="Online Shop">🛍️ Online Shop</Select.Option>
            <Select.Option value="Draft">📝 Draft</Select.Option>
            <Select.Option value="Confirmed">✅ Confirmed</Select.Option>
            <Select.Option value="Processing">⚙️ Processing</Select.Option>
            <Select.Option value="Shipped">📦 Shipped</Select.Option>
            <Select.Option value="Completed">🌟 Completed</Select.Option>
            <Select.Option value="Cancelled">❌ Cancelled</Select.Option>
          </Select>
        </Space>

        <Space>
          {(searchTerm || statusFilter !== 'All') && (
            <Button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('All');
              }}
            >
              Clear Filters
            </Button>
          )}
        </Space>
      </div>

      {/* Results count + pagination */}
      <ListPagination
        total={orders.length}
        filtered={filteredOrders.length}
        page={safeListPage}
        pages={orderPages}
        onPageChange={setListPage}
        noun="orders"
      />

      <div className="list-table-panel">
      <div style={s.tableWrapper}>
        <table style={s.table}>
          <thead style={s.tableHeader}>
            <tr>
              <th style={s.th}>Order ID</th>
              <th style={s.th}>Customer</th>
              <th style={s.th}>Description</th>
              <th style={s.th}>Items</th>
              <th style={s.th}>Total</th>
              <th style={s.th}>Payment</th>
              <th style={s.th}>Date</th>
              <th style={s.th}>Status</th>
              <th style={s.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedOrders.length > 0 ? paginatedOrders.map(order => {
              const itemCount = order.items?.length || 0;

              return (
                <tr key={order.id} style={{
                  ...s.tableRow,
                  ...(isOnlineShopOrder(order) ? { background: '#fffbeb', borderLeft: '3px solid #f59e0b' } : {})
                }}>
                  <td style={s.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <strong>#{order.order_number || order.id}</strong>
                      {isOnlineShopOrder(order) && (
                        <span title="Online Shop Order" style={{ fontSize: 11, fontWeight: 700, background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', borderRadius: 6, padding: '1px 7px', whiteSpace: 'nowrap' }}>
                          🛍️ Shop
                        </span>
                      )}
                      {order.has_backordered && (
                        <span title="Contains backordered items" style={{ fontSize: 11, fontWeight: 700, background: '#fff7ed', color: '#c2410c', border: '1px solid #fdba74', borderRadius: 6, padding: '1px 7px', whiteSpace: 'nowrap' }}>
                          ⚠️ BO
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={s.td}>
                    {order.customerName || order.client_name || <span style={{color: '#999', fontStyle: 'italic'}}>Guest</span>}
                  </td>
                  <td style={s.td}>
                    <div style={s.description}>
                      {isOnlineShopOrder(order)
                        ? <span style={{ color: '#b45309', fontWeight: 600 }}>🛍️ Online Shop Order</span>
                        : (order.notes || '-')}
                    </div>
                  </td>
                  <td style={s.td}>{itemCount} item{itemCount !== 1 ? 's' : ''}</td>
                  <td style={s.td}>
                    <strong>£{Number(order.total || 0).toFixed(2)}</strong>
                    {order.deposit_amount && Number(order.deposit_amount) > 0 && (
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>Deposit: £{Number(order.deposit_amount).toFixed(2)}</div>
                    )}
                  </td>
                  <td style={s.td}>
                    <div style={{ fontSize: 12 }}>
                      <span style={{ display: 'block', fontWeight: 600 }}>{order.paymentMethod || order.payment_method || '—'}</span>
                      <span style={{
                        display: 'inline-block', marginTop: 2, padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                        background:
                          (order.paymentStatus || order.payment_status) === 'Paid' ? '#f0fdf4' :
                          (order.paymentStatus || order.payment_status) === 'Failed' ? '#fef2f2' : '#fff7ed',
                        color:
                          (order.paymentStatus || order.payment_status) === 'Paid' ? '#10b981' :
                          (order.paymentStatus || order.payment_status) === 'Failed' ? '#ef4444' : '#f59e0b'
                      }}>{order.paymentStatus || order.payment_status || 'Pending'}</span>
                    </div>
                  </td>
                  <td style={s.td}>
                    {formatOrderDate(order)}
                  </td>
                  <td style={s.td}>
                    <span style={{
                      ...s.statusBadge,
                      background: 
                        order.status === 'Completed' ? '#52c41a' :
                        order.status === 'Cancelled' ? '#ff4d4f' :
                        order.status === 'Shipped' ? '#1890ff' :
                        order.status === 'Processing' ? '#722ed1' :
                        order.status === 'Confirmed' ? '#10b981' :
                        order.status === 'Draft' ? '#6b7280' :
                        '#fa8c16'
                    }}>
                      {order.status}
                    </span>
                  </td>
                  <td style={s.td}>
                    {renderOrderActions(order)}
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan={9} style={{...s.td, textAlign: 'center', padding: 40, color: '#999'}}>
                  {orders.length === 0 
                    ? '📭 No orders found. Create your first order!'
                    : '🔍 No orders match your filters. Try adjusting your search.'
                  }
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </div>

      <div className="list-mobile-cards">
        {paginatedOrders.length > 0 ? paginatedOrders.map(order => {
          const itemCount = order.items?.length || 0;
          return (
            <div key={order.id} className="list-mobile-card" style={isOnlineShopOrder(order) ? { borderLeft: '3px solid #f59e0b', background: '#fffbeb' } : undefined}>
              <div className="list-mobile-card-header">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="list-mobile-card-title">#{order.order_number || order.id}</div>
                  <div className="list-mobile-card-meta">
                    {order.customerName || order.client_name || 'Guest'} · {formatOrderDate(order)}
                  </div>
                </div>
                <span style={{
                  ...s.statusBadge,
                  background:
                    order.status === 'Completed' ? '#52c41a' :
                    order.status === 'Cancelled' ? '#ff4d4f' :
                    order.status === 'Shipped' ? '#1890ff' :
                    order.status === 'Processing' ? '#722ed1' :
                    order.status === 'Confirmed' ? '#10b981' :
                    order.status === 'Draft' ? '#6b7280' :
                    '#fa8c16'
                }}>{order.status}</span>
              </div>
              <div className="list-mobile-card-info">
                <div><div className="list-mobile-card-label">Items</div>{itemCount}</div>
                <div><div className="list-mobile-card-label">Total</div>£{Number(order.total || 0).toFixed(2)}</div>
                <div><div className="list-mobile-card-label">Payment</div>{order.paymentStatus || order.payment_status || 'Pending'}</div>
                <div><div className="list-mobile-card-label">Description</div>{isOnlineShopOrder(order) ? 'Online Shop' : (order.notes || '—')}</div>
              </div>
              <div className="list-mobile-actions">{renderOrderActions(order)}</div>
            </div>
          );
        }) : (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            {orders.length === 0
              ? '📭 No orders found. Create your first order!'
              : '🔍 No orders match your filters. Try adjusting your search.'}
          </div>
        )}
      </div>
      </Card>

      {filteredOrders.length > 0 && (
        <div style={s.stats}>
          <div style={s.statCard}>
            <div style={s.statLabel}>Total Orders</div>
            <div style={s.statValue}>{filteredOrders.length}</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statLabel}>Draft Orders</div>
            <div style={{...s.statValue, color: '#FF851B'}}>
              {filteredOrders.filter(o => o.status === 'Draft').length}
            </div>
          </div>
          <div style={s.statCard}>
            <div style={s.statLabel}>Completed Orders</div>
            <div style={{...s.statValue, color: '#2ECC40'}}>
              {filteredOrders.filter(o => o.status === 'Completed').length}
            </div>
          </div>
          <div style={s.statCard}>
            <div style={s.statLabel}>Total Revenue</div>
            <div style={s.statValue}>
              £{Number(filteredOrders.reduce((sum, o) => sum + Number(o.total || 0), 0)).toFixed(2)}
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

const s: any = {
  filterBar: {
    display: 'flex',
    gap: 12,
    marginBottom: 20,
    flexWrap: 'wrap',
    alignItems: 'center',
    background: '#fff',
    padding: 20,
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  searchInput: {
    flex: '1 1 300px',
    padding: '12px 16px',
    border: '2px solid #e0e0e0',
    borderRadius: 8,
    fontSize: 14,
    outline: 'none',
    transition: 'border-color 0.2s',
    fontFamily: 'Arial, sans-serif'
  },
  filterSelect: {
    padding: '12px 16px',
    border: '2px solid #e0e0e0',
    borderRadius: 8,
    fontSize: 14,
    outline: 'none',
    cursor: 'pointer',
    background: '#fff',
    transition: 'border-color 0.2s',
    fontFamily: 'Arial, sans-serif',
    minWidth: 150
  },
  clearButton: {
    padding: '12px 20px',
    background: '#FF4136',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    transition: 'background 0.2s'
  },
  resultsInfo: {
    fontSize: 14,
    color: '#6c757d',
    marginBottom: 15,
    fontWeight: 500
  },
  description: {
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: 13,
    color: '#666'
  },
  tableWrapper: { backgroundColor: '#fff', borderRadius: 2, border: '1px solid #f0f0f0', overflow: 'hidden', overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginTop: 16 },
  table: { width: '100%', minWidth: 700, borderCollapse: 'collapse' },
  tableHeader: { backgroundColor: '#fafafa', borderBottom: '1px solid #f0f0f0' },
  th: { padding: 16, textAlign: 'left', fontWeight: 500, fontSize: 14, color: 'rgba(0, 0, 0, 0.85)', whiteSpace: 'nowrap' },
  tableRow: { borderBottom: '1px solid #f0f0f0', transition: 'background 0.2s' },
  td: { padding: 16, fontSize: 14, color: 'rgba(0, 0, 0, 0.85)' },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  statusBadge: { display: 'inline-block', padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, color: '#fff', textTransform: 'capitalize' },
  stats: {display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 20, marginTop: 30},
  statCard: {background: '#fff', padding: 20, borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center'},
  statLabel: {fontSize: 13, color: '#6c757d', marginBottom: 8, fontWeight: 600},
  statValue: {fontSize: 24, fontWeight: 700, color: '#333'},
  filterBanner: {
    background: '#e7f3ff',
    padding: '12px 20px',
    borderRadius: 8,
    marginBottom: 20,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: '1px solid #b3d7ff'
  },
  filterText: {
    fontSize: 15,
    color: '#004085'
  },
  clearFilterButton: {
    background: '#007bff',
    color: '#fff',
    border: 'none',
    padding: '6px 12px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600
  }
};

export default OrderList;